import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { PRODUCT_STATUS_LABELS, label } from '@/lib/labels';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label as FormLabel } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Package, Plus, Loader2, Pencil, Trash2, Save, X, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import AgentActivityPanel from '@/components/AgentActivityPanel';
import EmptyState from '@/components/EmptyState';

const STATUS_COLORS = { in_sviluppo: 'bg-slate-100 text-slate-700', beta: 'bg-purple-100 text-purple-700', in_lancio: 'bg-blue-100 text-blue-700', active: 'bg-emerald-100 text-emerald-700', attivo: 'bg-emerald-100 text-emerald-700', stagionale: 'bg-amber-100 text-amber-700', sospeso: 'bg-orange-100 text-orange-700', ritirato: 'bg-red-100 text-red-700' };
const STATUS_LIST = ['in_sviluppo', 'beta', 'in_lancio', 'active', 'stagionale', 'sospeso', 'ritirato'];
const CATEGORY_LIST = [
  { value: 'coaching_individuale', label: 'Coaching Individuale' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'formazione_aziendale', label: 'Formazione Aziendale' },
  { value: 'consulenza', label: 'Consulenza' },
  { value: 'evento', label: 'Evento' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'abbonamento', label: 'Abbonamento' },
  { value: 'altro', label: 'Altro' },
];
const PRICING_MODELS = [
  { value: 'orario', label: 'Tariffa oraria' },
  { value: 'sessione', label: 'Per sessione' },
  { value: 'pacchetto', label: 'Pacchetto (N sessioni)' },
  { value: 'giornata', label: 'Tariffa giornaliera' },
  { value: 'evento', label: 'Per evento' },
  { value: 'per_participant', label: 'Per partecipante' },
  { value: 'one_time', label: 'Una tantum' },
  { value: 'recurring_monthly', label: 'Abbonamento mensile' },
  { value: 'free', label: 'Gratuito' },
];

const EMPTY_FORM = { name: '', description_short: '', category: 'coaching_individuale', pricing_model: 'orario', price_per_hour: '', price_total: '', sessions_included: '', session_duration_min: '', price_per_participant: '', currency: 'CHF', delivery_mode: 'online', language: 'it', max_participants: '', min_participants: '' };

function PriceFields({ form, setForm }) {
  const m = form.pricing_model;
  return (
    <div className="grid grid-cols-2 gap-3">
      {m === 'orario' && <div className="space-y-1"><FormLabel className="text-xs">Prezzo/ora (CHF)</FormLabel><Input type="number" value={form.price_per_hour} onChange={(e) => setForm({ ...form, price_per_hour: e.target.value })} /></div>}
      {m === 'sessione' && <><div className="space-y-1"><FormLabel className="text-xs">Prezzo/sessione (CHF)</FormLabel><Input type="number" value={form.price_total} onChange={(e) => setForm({ ...form, price_total: e.target.value })} /></div><div className="space-y-1"><FormLabel className="text-xs">Durata sessione (min)</FormLabel><Input type="number" value={form.session_duration_min} onChange={(e) => setForm({ ...form, session_duration_min: e.target.value })} /></div></>}
      {m === 'pacchetto' && <><div className="space-y-1"><FormLabel className="text-xs">Prezzo totale (CHF)</FormLabel><Input type="number" value={form.price_total} onChange={(e) => setForm({ ...form, price_total: e.target.value })} /></div><div className="space-y-1"><FormLabel className="text-xs">Sessioni incluse</FormLabel><Input type="number" value={form.sessions_included} onChange={(e) => setForm({ ...form, sessions_included: e.target.value })} /></div></>}
      {['giornata', 'evento', 'one_time', 'recurring_monthly'].includes(m) && <div className="space-y-1"><FormLabel className="text-xs">{m === 'recurring_monthly' ? 'Canone mensile (CHF)' : 'Prezzo totale (CHF)'}</FormLabel><Input type="number" value={form.price_total} onChange={(e) => setForm({ ...form, price_total: e.target.value })} /></div>}
      {m === 'per_participant' && <div className="space-y-1"><FormLabel className="text-xs">Prezzo/partecipante (CHF)</FormLabel><Input type="number" value={form.price_per_participant} onChange={(e) => setForm({ ...form, price_per_participant: e.target.value })} /></div>}
    </div>
  );
}

function getPrice(p) {
  if (p.pricing_model === 'free' || p.price_model === 'free') return 'Gratuito';
  if (p.price_per_hour) return `${p.price_per_hour} CHF/h`;
  if (p.price_total) return `${p.price_total} CHF`;
  if (p.price_chf) return `${p.price_chf} CHF`;
  if (p.price_per_participant) return `${p.price_per_participant} CHF/pers.`;
  return '-';
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const initialStatus = searchParams.get('status') || '';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [productEvents, setProductEvents] = useState([]);
  const [productBuyers, setProductBuyers] = useState([]);

  useEffect(() => { fetchProducts(); }, [statusFilter, typeFilter]);

  // Handle ?open= URL parameter
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam && products.length > 0) {
      const prod = products.find(p => p.id === openParam);
      if (prod) openProduct(prod);
    }
  }, [searchParams, products]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.category = typeFilter;
      const res = await api.get('/offering/products', { params });
      setProducts(res.data);
    } catch {} finally { setLoading(false); }
  };

  const createProduct = async () => {
    if (!form.name.trim()) { toast.error('Nome obbligatorio'); return; }
    try {
      await api.post('/offering/products', form);
      toast.success('Prodotto creato');
      setCreateOpen(false);
      setForm({ ...EMPTY_FORM });
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const openProduct = async (p) => {
    setSelected(p); setDrawerOpen(true); setEditMode(false);
    setProductEvents([]); setProductBuyers([]);
    try {
      const [evRes, buyRes] = await Promise.all([
        api.get(`/offering/products/${p.id}/events`).catch(() => ({ data: [] })),
        api.get(`/offering/products/${p.id}/buyers`).catch(() => ({ data: [] })),
      ]);
      setProductEvents(evRes.data || []);
      setProductBuyers(buyRes.data || []);
    } catch {}
  };

  const startEdit = () => {
    setEditData({ ...selected });
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editData };
      delete payload.id; delete payload.organization_id; delete payload.created_at;
      await api.patch(`/offering/products/${selected.id}`, payload);
      toast.success('Prodotto aggiornato');
      setEditMode(false);
      fetchProducts();
      setSelected({ ...selected, ...editData });
    } catch { toast.error('Errore aggiornamento'); }
  };

  const deleteProduct = async (id) => {
    const ok = await confirm({
      title: 'Eliminare questo prodotto?',
      description: 'Questa azione non può essere annullata. Il prodotto sarà rimosso definitivamente dal catalogo.',
      variant: 'destructive'
    });
    if (!ok) return;
    try {
      await api.delete(`/offering/products/${id}`);
      toast.success('Prodotto eliminato');
      setDrawerOpen(false); setSelected(null);
      fetchProducts();
    } catch { toast.error('Errore eliminazione'); }
  };

  const changeStatus = async (productId, newStatus) => {
    try {
      await api.patch(`/offering/products/${productId}/status`, { status: newStatus });
      toast.success('Stato aggiornato');
      fetchProducts();
      if (selected?.id === productId) setSelected(prev => ({ ...prev, status: newStatus }));
    } catch { toast.error('Errore'); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...products].sort((a, b) => {
    const va = a[sortField] || '';
    const vb = b[sortField] || '';
    const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const catLabel = (v) => CATEGORY_LIST.find(c => c.value === v)?.label || v || '-';
  const pricingLabel = (v) => PRICING_MODELS.find(m => m.value === v)?.label || v || '-';

  return (
    <>
    <ConfirmDialog />
    <div className="animate-fade-in" data-testid="products-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Catalogo Prodotti</h1>
          <p className="page-subtitle">Il tuo catalogo di servizi e prodotti. Definisci prezzi, formato e stato di ciascuna offerta. Clicca su un prodotto per modificarlo.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white flex-shrink-0" data-testid="create-product-btn"><Plus className="w-4 h-4 mr-2" />Nuovo Prodotto</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-[#1A3A5C]">Nuovo Prodotto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><FormLabel className="text-xs">Nome <span className="text-red-500">*</span></FormLabel><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={!form.name.trim() ? 'border-red-300 focus:border-red-500' : ''} data-testid="product-name-input" /></div>
              <div className="space-y-1"><FormLabel className="text-xs">Descrizione</FormLabel><Textarea value={form.description_short} onChange={(e) => setForm({ ...form, description_short: e.target.value })} rows={2} data-testid="product-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><FormLabel className="text-xs">Categoria <span className="text-red-500">*</span></FormLabel>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORY_LIST.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1"><FormLabel className="text-xs">Modello Pricing <span className="text-red-500">*</span></FormLabel>
                  <Select value={form.pricing_model} onValueChange={(v) => setForm({ ...form, pricing_model: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRICING_MODELS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">{
                    form.pricing_model === 'orario' ? 'Es: 150 CHF/h per coaching 1:1' :
                    form.pricing_model === 'sessione' ? 'Es: 300 CHF per sessione di 90 min' :
                    form.pricing_model === 'pacchetto' ? 'Es: 1200 CHF per 6 sessioni incluse' :
                    form.pricing_model === 'giornata' ? 'Es: 2000 CHF per giornata formativa' :
                    form.pricing_model === 'per_participant' ? 'Es: 80 CHF per partecipante' :
                    form.pricing_model === 'evento' ? 'Es: 5000 CHF per evento completo' :
                    form.pricing_model === 'recurring_monthly' ? 'Es: 500 CHF/mese abbonamento' :
                    form.pricing_model === 'free' ? 'Nessun costo per il cliente' :
                    form.pricing_model === 'one_time' ? 'Es: 1000 CHF pagamento unico' : ''
                  }</p>
                </div>
              </div>
              <PriceFields form={form} setForm={setForm} />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><FormLabel className="text-xs">Max partecipanti</FormLabel><Input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} /></div>
                <div className="space-y-1"><FormLabel className="text-xs">Formato</FormLabel>
                  <Select value={form.delivery_mode} onValueChange={(v) => setForm({ ...form, delivery_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="presenza">In presenza</SelectItem><SelectItem value="ibrido">Ibrido</SelectItem></SelectContent></Select>
                </div>
              </div>
              <Button onClick={createProduct} className="w-full bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid="submit-product-btn">Crea Prodotto</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <AgentActivityPanel
        agents={['offering_agent']}
        areaLabel="Catalogo prodotti"
      />

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="products-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{STATUS_LIST.map(s => <SelectItem key={s} value={s}>{label(PRODUCT_STATUS_LABELS, s)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="products-type-filter"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le categorie</SelectItem>{CATEGORY_LIST.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-[#1A3A5C]" /></div>
      ) : products.length === 0 ? (
        <EmptyState icon={Package} title="Nessun prodotto nel catalogo"
          description="Aggiungi i tuoi servizi: eventi, formazione, coaching, consulenze, prodotti digitali. Il catalogo e consultato da tutti gli agenti per proporre l'offerta giusta al contatto giusto."
          actionLabel="Crea il primo prodotto" onAction={() => setCreateOpen(true)} />
      ) : (
        <Card className="border-[#E2E8F0]">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs uppercase cursor-pointer hover:text-[#1A3A5C]" onClick={() => toggleSort('name')} data-testid="sort-name">
                  Nome {sortField === 'name' && <ArrowUpDown className="w-3 h-3 inline ml-1" />}
                </TableHead>
                <TableHead className="text-xs uppercase cursor-pointer hover:text-[#1A3A5C]" onClick={() => toggleSort('category')}>
                  Categoria {sortField === 'category' && <ArrowUpDown className="w-3 h-3 inline ml-1" />}
                </TableHead>
                <TableHead className="text-xs uppercase">Pricing</TableHead>
                <TableHead className="text-xs uppercase">Prezzo</TableHead>
                <TableHead className="text-xs uppercase">Formato</TableHead>
                <TableHead className="text-xs uppercase cursor-pointer hover:text-[#1A3A5C]" onClick={() => toggleSort('status')}>
                  Stato {sortField === 'status' && <ArrowUpDown className="w-3 h-3 inline ml-1" />}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(p => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-[#F8FAFC]" onClick={() => openProduct(p)} data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <span className="font-medium text-[#0F172A]">{p.name}</span>
                    {p.description_short && <p className="text-xs text-[#64748B] line-clamp-1">{p.description_short}</p>}
                  </TableCell>
                  <TableCell className="text-sm">{catLabel(p.category || p.product_type)}</TableCell>
                  <TableCell className="text-sm text-[#64748B]">{pricingLabel(p.pricing_model || p.price_model)}</TableCell>
                  <TableCell className="text-sm font-medium">{getPrice(p)}</TableCell>
                  <TableCell className="text-sm text-[#64748B]">{p.delivery_mode || p.delivery_format || '-'}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-700'} text-xs`}>{label(PRODUCT_STATUS_LABELS, p.status)}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={(o) => { setDrawerOpen(o); if (!o) setEditMode(false); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="product-drawer">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="text-[#1A3A5C] text-lg">{selected?.name}</SheetTitle>
              <div className="flex gap-1.5">
                {editMode ? (
                  <>
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit} data-testid="save-product-btn"><Save className="w-3 h-3 mr-1" />Salva</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(false)}><X className="w-3 h-3 mr-1" />Annulla</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEdit} data-testid="edit-product-btn"><Pencil className="w-3 h-3 mr-1" />Modifica</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => deleteProduct(selected?.id)} data-testid="delete-product-btn"><Trash2 className="w-3 h-3 mr-1" />Elimina</Button>
                  </>
                )}
              </div>
            </div>
            <SheetDescription>{selected?.description_short}</SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-4">
              {editMode ? (
                <div className="space-y-3">
                  <div className="space-y-1"><FormLabel className="text-xs">Nome</FormLabel><Input value={editData.name || ''} onChange={e => setEditData({...editData, name: e.target.value})} /></div>
                  <div className="space-y-1"><FormLabel className="text-xs">Descrizione</FormLabel><Textarea value={editData.description_short || ''} onChange={e => setEditData({...editData, description_short: e.target.value})} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><FormLabel className="text-xs">Categoria</FormLabel>
                      <Select value={editData.category || ''} onValueChange={v => setEditData({...editData, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORY_LIST.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="space-y-1"><FormLabel className="text-xs">Modello Pricing</FormLabel>
                      <Select value={editData.pricing_model || ''} onValueChange={v => setEditData({...editData, pricing_model: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRICING_MODELS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <PriceFields form={editData} setForm={setEditData} />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><FormLabel className="text-xs">Max partecipanti</FormLabel><Input type="number" value={editData.max_participants || ''} onChange={e => setEditData({...editData, max_participants: e.target.value})} /></div>
                    <div className="space-y-1"><FormLabel className="text-xs">Formato</FormLabel>
                      <Select value={editData.delivery_mode || 'online'} onValueChange={v => setEditData({...editData, delivery_mode: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="presenza">In presenza</SelectItem><SelectItem value="ibrido">Ibrido</SelectItem></SelectContent></Select>
                    </div>
                  </div>
                  <div className="space-y-1"><FormLabel className="text-xs">Stato</FormLabel>
                    <Select value={editData.status || ''} onValueChange={v => setEditData({...editData, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUS_LIST.map(s => <SelectItem key={s} value={s}>{label(PRODUCT_STATUS_LABELS, s)}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-[#64748B] text-xs block">Categoria</span><span className="font-medium">{catLabel(selected.category || selected.product_type)}</span></div>
                    <div><span className="text-[#64748B] text-xs block">Pricing</span><span className="font-medium">{pricingLabel(selected.pricing_model || selected.price_model)}</span></div>
                    <div><span className="text-[#64748B] text-xs block">Prezzo</span><span className="font-medium text-[#1A3A5C]">{getPrice(selected)}</span></div>
                    <div><span className="text-[#64748B] text-xs block">Formato</span><span className="font-medium">{selected.delivery_mode || selected.delivery_format || '-'}</span></div>
                    <div><span className="text-[#64748B] text-xs block">Lingua</span><span className="font-medium">{selected.language || 'it'}</span></div>
                    <div><span className="text-[#64748B] text-xs block">Max partecipanti</span><span className="font-medium">{selected.max_participants || '-'}</span></div>
                  </div>
                  <div className="pt-3 border-t border-[#E2E8F0]">
                    <span className="text-[#64748B] text-xs block mb-2">Stato</span>
                    <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_LIST.map(s => <SelectItem key={s} value={s}>{label(PRODUCT_STATUS_LABELS, s)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {/* Cross-ref: eventi collegati */}
                  <div className="pt-3 border-t border-[#E2E8F0]">
                    <h4 className="text-xs font-semibold text-[#1A3A5C] uppercase tracking-wider mb-2">Eventi collegati ({productEvents.length})</h4>
                    {productEvents.length === 0 ? (
                      <p className="text-xs text-[#94A3B8]">Nessun evento collegato</p>
                    ) : productEvents.map(ev => (
                      <div key={ev.id} className="flex items-center justify-between p-2 border rounded mb-1 text-xs cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                        onClick={() => { setDrawerOpen(false); navigate(`/events?open=${ev.id}`); }}>
                        <span className="font-medium text-[#2E75B6] hover:underline">{ev.name}</span>
                        <span className="text-[#94A3B8]">{ev.event_date?.slice(0, 10) || '-'}</span>
                      </div>
                    ))}
                  </div>
                  {/* Cross-ref: acquirenti */}
                  <div className="pt-3 border-t border-[#E2E8F0]">
                    <h4 className="text-xs font-semibold text-[#1A3A5C] uppercase tracking-wider mb-2">Acquirenti ({productBuyers.length})</h4>
                    {productBuyers.length === 0 ? (
                      <p className="text-xs text-[#94A3B8]">Nessun acquisto registrato</p>
                    ) : productBuyers.map(d => (
                      <div key={d.id} className="flex items-center justify-between p-2 border rounded mb-1 text-xs cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                        onClick={() => { setDrawerOpen(false); navigate(`/contacts?open=${d.contact_id}`); }}>
                        <span className="font-medium text-[#2E75B6] hover:underline">{d.contact_name || 'Contatto'}</span>
                        <span className="text-[#94A3B8]">{d.estimated_value_chf ? `${d.estimated_value_chf} CHF` : '-'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
    </>
  );
}
